import { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import styled from 'styled-components';
import moment from 'moment';
import { getAttachments } from '../../api/attachments';
import { excelStyles } from '../../components/Grid/ExcelStyles/excelStyles';
import LoadRowsComponent from '../../helpers/LoadRowsComponent';
import StudyLinkForeignKey from '../../components/Grid/CellRenderer/StudyLinkForeignKey';
import TotalRow from '../../components/Grid/CellRenderer/TotalRow';

const AttachmentsGrid = ({
  attachmentList,
  search,
  onRowClicked = () => {},
  onDeleteClicked = () => {},
  setRefresh,
  refresh,
  sort = "",
  setSort = () => {},
  isStudyDetail = false,
}) => {
  const [gridApi, setGridApi] = useState(null);
  const [totalRows, setTotalRows] = useState(attachmentList ? attachmentList.length : 0);

  useEffect(() => {
    if(attachmentList){
      setTotalRows(attachmentList.length)
    }
  }, [attachmentList]);

  useEffect(() => {
    if(refresh){
      let refreshOptions = {}
      // if adding the first row, purge for item to properly refresh
      // only when attachmentList grid data not controlled, ie, attachmentList not set
      if (gridApi.getDisplayedRowCount() === 0) refreshOptions = { purge: true };
      gridApi.refreshServerSideStore(refreshOptions);
      setRefresh(false);
    }
  }, [refresh]);

  useEffect(() => {
    if(gridApi){
      //loads in more rows while scrolling
      const dataSource = {
        getRows: function(params) {
          let sortParam = "";
          if(params.request.sortModel.length !== 0){
            const sortColumn = params.request.sortModel[0].colId;
            const sortWay = params.request.sortModel[0].sort;
            if(sortWay === "desc"){
              sortParam += "-";
            }
            if(sortColumn === "org_study_id"){
              sortParam += `study__${sortColumn}`
            }
            else if(sortColumn.includes('attachment')){
              sortParam += "attachment"
            }
            else{
              sortParam += sortColumn;
              sortParam = sortParam.replaceAll(".", "__");
            }
          }

          if(sortParam){
            setSort(sortParam);
          }
          else{
            setSort("");
          }
          const rows = getAttachments({limit: 100, offset: params.request.startRow, search: search, sort: sort})
            .then( res => {
              setTotalRows(res.count);
              params.success({
                rowData: res.results,
                rowCount: res.count
              })
              if(res.count === 0){
                gridApi.showNoRowsOverlay();
              }
            });
        }
      };
      gridApi.setServerSideDatasource(dataSource);
    }
  }, [gridApi, search, sort]);

  
  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const cellClicked = (event) => {
    const { cellRenderer } = event.colDef;
    if (cellRenderer !== "removeRow") {
      onRowClicked(event.data);
    }
  };

  const deleteRow = (itemId) => {
    onDeleteClicked(itemId);
  };

  const onFilterChanged = () => {
    if (gridApi) {
      const displayedRowCount = gridApi.getDisplayedRowCount();
      setTotalRows(displayedRowCount);
    }
  };

  const gridOptions = {
    columnDefs: [
      {
        headerName: 'Study Id',
        field: 'study.org_study_id',
        hide: isStudyDetail,
        cellRenderer: "studyLink",
        flex: '1'
      },
      {
        headerName: 'Name',
        field: 'name',
        flex: '1'
      },
      {
        headerName: 'Type',
        field: 'type',
        flex: '1'
      },
      {
        headerName: "Language",
        field: "language_name",
        flex: 1
      },
      {
        headerName: 'File URL',
        cellRenderer: "urlRef",
        field: "attachment_info.url",
        flex: '1'
      },
      {
        headerName: 'File Name',
        field: 'attachment_info.title',
        flex: '1'
      },
      {
        headerName: 'Updated At',
        cellRenderer: "updatedTime",
        field: 'updated_at',
        flex: '1'
      },
      {
        headerName: 'User Email',
        // cellRenderer: "user.email",
        field: 'user.email',
        flex: '1'
      },
      {
        headerName: "",
        width: 40,
        cellRenderer: "removeRow",
      }
    ],
    defaultColDef: {
      resizable: true,
      sortable: true,
      wrapText: true,
      filter: true,
      enablePivot: true,
      enableCharts: false,
      enableRowGroup: true,
      filterParams: {
        clearButton: true
      },
      cellStyle: { "white-space": "normal !important" },
      autoHeight: true,
      cellClassRules: {
        oddBackground: function (params) {
          return params.rowIndex % 2 == 0;
        },
      },
    },
    excelStyles,
    frameworkComponents: {
      updatedTime: ({ data }) => {
        const updated_at = data?.updated_at;
        return (
          <span>{moment(updated_at).format("DD-MMM-YYYY, HH:mm")}</span>
        );
      },
      removeRow: ({ data }) => {
        const id = data?.id;
        return (
          <i
            className="fal fa-trash-alt"
            style={{ color: "red" }}
            onClick={() => deleteRow(data)}
          ></i>

        );
      },
      urlRef: ({ data }) => {
        const attachment_info = data?.attachment_info;
        return(
          <a href={attachment_info?.url} target="_blank">{attachment_info?.url}</a>
        )
      },
      LoadRowsComponent: LoadRowsComponent,
      studyLink: StudyLinkForeignKey,
    },
    loadingCellRenderer: 'LoadRowsComponent',
    loadingCellRendererParams :{
      loadingMessage: 'One moment please...',
    },
  };

  return (
    <Container className="ag-theme-balham">
      { isStudyDetail
        ?
        <AgGridReact
        onGridReady={onGridReady}
        gridOptions={gridOptions}
        onCellClicked={event => cellClicked(event)}
        onFilterChanged={onFilterChanged}
        rowData={attachmentList}
      ></AgGridReact>
        :
        <AgGridReact
        onGridReady={onGridReady}
        gridOptions={gridOptions}
        onCellClicked={event => cellClicked(event)}
        onFilterChanged={onFilterChanged}
        rowModelType={'serverSide'}
        serverSideStoreType={'partial'}
        cacheBlockSize={100}
      ></AgGridReact>
      }
    <TotalRow count={totalRows} />
    </Container>
  );
};

export default AttachmentsGrid;

const Container = styled.div`
flex: 1 1 auto;
padding-bottom: 20px;
`